import { clamp } from "es-toolkit";

import type { BoardBox, BoardPoint, ShelfBoardView } from "@/shelf/types/types";

import {
  BOARD_PIXELS_PER_MILLIMETER,
  MAXIMUM_BOARD_ZOOM_PERCENTAGE,
  MINIMUM_BOARD_ZOOM_PERCENTAGE,
} from "@/shelf/constants/shelf-geometry";

/**
 * How the board is projected, in one place.
 *
 * Board space is millimeters and the projection is a scale and an offset, so a
 * point can be read back out of a pointer event by arithmetic. Nothing here
 * measures an element: the board answers "which racks are on screen" on every
 * frame of a pan, and a projection read from the DOM would force a layout.
 */

export const clampBoardZoomPercentage = (zoomPercentage: number) =>
  Number.isFinite(zoomPercentage)
    ? clamp(zoomPercentage, MINIMUM_BOARD_ZOOM_PERCENTAGE, MAXIMUM_BOARD_ZOOM_PERCENTAGE)
    : MINIMUM_BOARD_ZOOM_PERCENTAGE;

export const resolveBoardPixelsPerMillimeter = (zoomPercentage: number) =>
  (BOARD_PIXELS_PER_MILLIMETER * clampBoardZoomPercentage(zoomPercentage)) / 100;

export const boardPointFromViewport = ({
  view,
  xPixels,
  yPixels,
}: {
  view: ShelfBoardView;
  xPixels: number;
  yPixels: number;
}): BoardPoint => {
  const pixelsPerMillimeter = resolveBoardPixelsPerMillimeter(view.zoomPercentage);
  return {
    xMillimeters: (xPixels - view.panOffsetXPixels) / pixelsPerMillimeter,
    yMillimeters: (yPixels - view.panOffsetYPixels) / pixelsPerMillimeter,
  };
};

/**
 * Zooms with the point under the pointer nailed in place.
 *
 * That point is the one the reader is looking at, so it is the one that must
 * not move; anchoring to a corner instead slides the board out from under
 * whatever was being aimed at, which at the far end of this range is most of a
 * collection. The zoom is clamped before the offset is worked out, so a wheel
 * spun past the end of the range still holds the pointer still.
 */
export const zoomBoardAroundPoint = ({
  view,
  xPixels,
  yPixels,
  zoomPercentage,
}: {
  view: ShelfBoardView;
  xPixels: number;
  yPixels: number;
  zoomPercentage: number;
}): ShelfBoardView => {
  const anchor = boardPointFromViewport({ view, xPixels, yPixels });
  const clampedZoomPercentage = clampBoardZoomPercentage(zoomPercentage);
  const pixelsPerMillimeter = resolveBoardPixelsPerMillimeter(clampedZoomPercentage);
  return {
    panOffsetXPixels: xPixels - anchor.xMillimeters * pixelsPerMillimeter,
    panOffsetYPixels: yPixels - anchor.yMillimeters * pixelsPerMillimeter,
    zoomPercentage: clampedZoomPercentage,
  };
};

/** How much of the viewport a fitted board leaves as margin around itself. */
const FIT_MARGIN = 0.92;

/**
 * The view that puts a whole box on screen, centred.
 *
 * Null rather than a default when there is nothing to fit or nothing to fit it
 * into: a viewport with no size is what a board is before it has been laid out,
 * and a fit computed then divides by zero and strands the view somewhere the
 * reader cannot pan back from.
 */
export const resolveViewFittingBox = ({
  box,
  viewportWidthPixels,
  viewportHeightPixels,
}: {
  box: BoardBox | null;
  viewportWidthPixels: number;
  viewportHeightPixels: number;
}): ShelfBoardView | null => {
  if (
    !box ||
    box.widthMillimeters <= 0 ||
    box.heightMillimeters <= 0 ||
    viewportWidthPixels <= 0 ||
    viewportHeightPixels <= 0
  ) {
    return null;
  }
  const zoomPercentage = clampBoardZoomPercentage(
    (Math.min(
      viewportWidthPixels / box.widthMillimeters,
      viewportHeightPixels / box.heightMillimeters,
    ) *
      FIT_MARGIN *
      100) /
      BOARD_PIXELS_PER_MILLIMETER,
  );
  const pixelsPerMillimeter = resolveBoardPixelsPerMillimeter(zoomPercentage);
  return {
    panOffsetXPixels:
      viewportWidthPixels / 2 - (box.xMillimeters + box.widthMillimeters / 2) * pixelsPerMillimeter,
    panOffsetYPixels:
      viewportHeightPixels / 2 -
      (box.yMillimeters + box.heightMillimeters / 2) * pixelsPerMillimeter,
    zoomPercentage,
  };
};
